/**
 * Renderer.ts
 * Canvas rendering utilities with FF1-style pixel-perfect graphics
 */

import { Sprite, TextStyle } from './types/engine';

export class Renderer {
  private ctx: CanvasRenderingContext2D;
  private scale: number;
  private baseWidth: number;
  private baseHeight: number;

  constructor(ctx: CanvasRenderingContext2D, scale: number = 1) {
    this.ctx = ctx;
    this.scale = scale;
    this.baseWidth = 256; // NES resolution
    this.baseHeight = 240;

    // Disable image smoothing for pixel-perfect rendering
    this.ctx.imageSmoothingEnabled = false;
  }

  /**
   * Get the rendering context
   */
  getContext(): CanvasRenderingContext2D {
    return this.ctx;
  }

  /**
   * Update the rendering context (useful for scene changes)
   */
  setContext(ctx: CanvasRenderingContext2D): void {
    this.ctx = ctx;
    this.ctx.imageSmoothingEnabled = false;
  }

  /**
   * Clear the canvas
   */
  clear(color: string = '#000000'): void {
    this.ctx.fillStyle = color;
    this.ctx.fillRect(0, 0, this.ctx.canvas.width, this.ctx.canvas.height);
  }

  /**
   * Draw a sprite at the specified position
   */
  drawSprite(sprite: Sprite, x: number, y: number, flipX: boolean = false): void {
    if (!sprite || !sprite.image) return;

    const scaledX = x * this.scale;
    const scaledY = y * this.scale;
    const scaledWidth = sprite.width * this.scale;
    const scaledHeight = sprite.height * this.scale;

    this.ctx.save();

    if (flipX) {
      this.ctx.translate(scaledX + scaledWidth, scaledY);
      this.ctx.scale(-1, 1);
      this.ctx.drawImage(
        sprite.image,
        sprite.x,
        sprite.y,
        sprite.width,
        sprite.height,
        0,
        0,
        scaledWidth,
        scaledHeight
      );
    } else {
      this.ctx.drawImage(
        sprite.image,
        sprite.x,
        sprite.y,
        sprite.width,
        sprite.height,
        scaledX,
        scaledY,
        scaledWidth,
        scaledHeight
      );
    }

    this.ctx.restore();
  }

  /**
   * Draw text with FF-style font (updated with TextStyle support)
   */
  drawText(
    text: string,
    x: number,
    y: number,
    style: Partial<TextStyle> | string = '#FFFFFF'
  ): void {
    // Handle legacy string color parameter
    const textStyle: TextStyle = typeof style === 'string'
      ? {
          font: 'ff1',
          fontSize: 8,
          color: style,
          align: 'left',
          baseline: 'top',
        }
      : {
          font: style.font || 'ff1',
          fontSize: style.fontSize || 8,
          color: style.color || '#FFFFFF',
          align: style.align || 'left',
          baseline: style.baseline || 'top',
          shadow: style.shadow || false,
          shadowColor: style.shadowColor || '#000000',
          shadowOffsetX: style.shadowOffsetX || 2,
          shadowOffsetY: style.shadowOffsetY || 2,
        };

    this.ctx.save();

    // Shadow
    if (textStyle.shadow) {
      this.ctx.fillStyle = textStyle.shadowColor!;
      this.ctx.font = `${textStyle.fontSize}px monospace`;
      this.ctx.textAlign = textStyle.align!;
      this.ctx.textBaseline = textStyle.baseline!;
      this.ctx.fillText(
        text,
        x + textStyle.shadowOffsetX!,
        y + textStyle.shadowOffsetY!
      );
    }

    // Main text
    this.ctx.fillStyle = textStyle.color;
    this.ctx.font = `${textStyle.fontSize}px monospace`;
    this.ctx.textAlign = textStyle.align!;
    this.ctx.textBaseline = textStyle.baseline!;
    this.ctx.fillText(text, x, y);

    this.ctx.restore();
  }

  /**
   * Draw FF1-style bordered window
   */
  drawWindow(x: number, y: number, width: number, height: number): void {
    const scaledX = x * this.scale;
    const scaledY = y * this.scale;
    const scaledWidth = width * this.scale;
    const scaledHeight = height * this.scale;
    const borderWidth = 2 * this.scale;

    this.ctx.save();

    // Background
    this.ctx.fillStyle = '#000080'; // Dark blue
    this.ctx.fillRect(scaledX, scaledY, scaledWidth, scaledHeight);

    // Outer border (white)
    this.ctx.strokeStyle = '#FFFFFF';
    this.ctx.lineWidth = borderWidth;
    this.ctx.strokeRect(
      scaledX + borderWidth / 2,
      scaledY + borderWidth / 2,
      scaledWidth - borderWidth,
      scaledHeight - borderWidth
    );

    // Inner border (slightly darker)
    const innerOffset = borderWidth * 1.5;
    this.ctx.strokeStyle = '#C0C0C0';
    this.ctx.lineWidth = 1;
    this.ctx.strokeRect(
      scaledX + innerOffset,
      scaledY + innerOffset,
      scaledWidth - innerOffset * 2,
      scaledHeight - innerOffset * 2
    );

    this.ctx.restore();
  }

  /**
   * Draw HP bar (updated signature with optional custom color)
   */
  drawHPBar(
    x: number,
    y: number,
    width: number,
    height: number,
    current: number,
    max: number,
    customColor?: string
  ): void {
    const scaledX = x * this.scale;
    const scaledY = y * this.scale;
    const scaledWidth = width * this.scale;
    const scaledHeight = height * this.scale;

    const percent = Math.max(0, Math.min(1, current / max));

    this.ctx.save();

    // Background
    this.ctx.fillStyle = '#400000';
    this.ctx.fillRect(scaledX, scaledY, scaledWidth, scaledHeight);

    // HP fill - use custom color or auto-color based on percentage
    const fillWidth = scaledWidth * percent;
    if (customColor) {
      this.ctx.fillStyle = customColor;
    } else if (percent > 0.5) {
      this.ctx.fillStyle = '#00FF00'; // Green
    } else if (percent > 0.25) {
      this.ctx.fillStyle = '#FFFF00'; // Yellow
    } else {
      this.ctx.fillStyle = '#FF0000'; // Red
    }
    this.ctx.fillRect(scaledX, scaledY, fillWidth, scaledHeight);

    // Border
    this.ctx.strokeStyle = '#FFFFFF';
    this.ctx.lineWidth = 1;
    this.ctx.strokeRect(scaledX, scaledY, scaledWidth, scaledHeight);

    this.ctx.restore();
  }

  /**
   * Draw MP bar (similar to HP but blue)
   * Now with consistent signature: x, y, width, height, current, max
   */
  drawMPBar(
    x: number,
    y: number,
    width: number,
    height: number,
    current: number,
    max: number
  ): void {
    const scaledX = x * this.scale;
    const scaledY = y * this.scale;
    const scaledWidth = width * this.scale;
    const scaledHeight = height * this.scale;

    const percent = Math.max(0, Math.min(1, current / max));

    this.ctx.save();

    // Background
    this.ctx.fillStyle = '#000040';
    this.ctx.fillRect(scaledX, scaledY, scaledWidth, scaledHeight);

    // MP fill
    const fillWidth = scaledWidth * percent;
    this.ctx.fillStyle = '#00CCFF'; // Cyan/Blue
    this.ctx.fillRect(scaledX, scaledY, fillWidth, scaledHeight);

    // Border
    this.ctx.strokeStyle = '#FFFFFF';
    this.ctx.lineWidth = 1;
    this.ctx.strokeRect(scaledX, scaledY, scaledWidth, scaledHeight);

    this.ctx.restore();
  }

  /**
   * Draw rectangle
   */
  drawRect(
    x: number,
    y: number,
    width: number,
    height: number,
    color: string,
    filled: boolean = true
  ): void {
    const scaledX = x * this.scale;
    const scaledY = y * this.scale;
    const scaledWidth = width * this.scale;
    const scaledHeight = height * this.scale;

    this.ctx.save();
    if (filled) {
      this.ctx.fillStyle = color;
      this.ctx.fillRect(scaledX, scaledY, scaledWidth, scaledHeight);
    } else {
      this.ctx.strokeStyle = color;
      this.ctx.lineWidth = 1;
      this.ctx.strokeRect(scaledX, scaledY, scaledWidth, scaledHeight);
    }
    this.ctx.restore();
  }

  /**
   * Draw cursor (triangle)
   */
  drawCursor(x: number, y: number, size: number = 4): void {
    const scaledX = x * this.scale;
    const scaledY = y * this.scale;
    const scaledSize = size * this.scale;

    this.ctx.save();
    this.ctx.fillStyle = '#FFFFFF';
    this.ctx.beginPath();
    this.ctx.moveTo(scaledX, scaledY);
    this.ctx.lineTo(scaledX + scaledSize, scaledY + scaledSize / 2);
    this.ctx.lineTo(scaledX, scaledY + scaledSize);
    this.ctx.closePath();
    this.ctx.fill();
    this.ctx.restore();
  }

  /**
   * Set rendering scale
   */
  setScale(scale: number): void {
    this.scale = scale;
  }

  /**
   * Get base dimensions
   */
  getBaseDimensions(): { width: number; height: number } {
    return { width: this.baseWidth, height: this.baseHeight };
  }

  /**
   * Flash effect (for damage)
   */
  flash(color: string = '#FFFFFF', alpha: number = 0.5): void {
    this.ctx.save();
    this.ctx.fillStyle = color;
    this.ctx.globalAlpha = alpha;
    this.ctx.fillRect(0, 0, this.ctx.canvas.width, this.ctx.canvas.height);
    this.ctx.restore();
  }

  /**
   * Draw damage number (floating text with style)
   */
  drawDamageNumber(
    x: number,
    y: number,
    damage: number,
    isCrit: boolean = false
  ): void {
    const color = isCrit ? '#ffff00' : '#ffffff';
    const fontSize = isCrit ? 12 : 10;

    this.drawText(damage.toString(), x, y, {
      font: 'ff1',
      fontSize,
      color,
      shadow: true,
      shadowColor: '#000000',
      shadowOffsetX: 1,
      shadowOffsetY: 1,
    });
  }

  /**
   * Draw animated sprite with frame support
   */
  drawAnimatedSprite(
    _spriteId: string,
    x: number,
    y: number,
    _frame: number = 0
  ): void {
    // This would need sprite manager integration
    // For now, draw a placeholder
    this.drawRect(x, y, 16, 16, '#ff0000', true);
  }

  /**
   * Draw stat bar (generic version for HP/MP/EXP)
   */
  drawStatBar(
    x: number,
    y: number,
    current: number,
    max: number,
    color: string,
    width: number = 48,
    height: number = 4
  ): void {
    const scaledX = x * this.scale;
    const scaledY = y * this.scale;
    const scaledWidth = width * this.scale;
    const scaledHeight = height * this.scale;

    const percent = Math.max(0, Math.min(1, current / max));

    this.ctx.save();

    // Background (darker version of color)
    this.ctx.fillStyle = this.darkenColor(color, 0.3);
    this.ctx.fillRect(scaledX, scaledY, scaledWidth, scaledHeight);

    // Fill
    const fillWidth = scaledWidth * percent;
    this.ctx.fillStyle = color;
    this.ctx.fillRect(scaledX, scaledY, fillWidth, scaledHeight);

    // Border
    this.ctx.strokeStyle = '#ffffff';
    this.ctx.lineWidth = 1;
    this.ctx.strokeRect(scaledX, scaledY, scaledWidth, scaledHeight);

    this.ctx.restore();
  }

  /**
   * Helper to darken a color
   */
  private darkenColor(color: string, factor: number): string {
    // Simple darkening for hex colors
    if (color.startsWith('#')) {
      const hex = color.slice(1);
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);

      const newR = Math.floor(r * factor);
      const newG = Math.floor(g * factor);
      const newB = Math.floor(b * factor);

      return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
    }
    return color;
  }

  /**
   * Draw centered text
   */
  drawCenteredText(
    text: string,
    x: number,
    y: number,
    style: Partial<TextStyle> | string = '#FFFFFF'
  ): void {
    const textStyle = typeof style === 'string' ? { color: style } : style;
    this.drawText(text, x, y, {
      ...textStyle,
      align: 'center',
    });
  }

  /**
   * Draw tile from tileset (for map rendering)
   */
  drawTile(
    tilesetImage: HTMLImageElement,
    tileId: number,
    x: number,
    y: number,
    tileSize: number = 16,
    tilesPerRow: number = 16
  ): void {
    const sourceX = (tileId % tilesPerRow) * tileSize;
    const sourceY = Math.floor(tileId / tilesPerRow) * tileSize;

    const scaledX = x * this.scale;
    const scaledY = y * this.scale;
    const scaledSize = tileSize * this.scale;

    this.ctx.drawImage(
      tilesetImage,
      sourceX,
      sourceY,
      tileSize,
      tileSize,
      scaledX,
      scaledY,
      scaledSize,
      scaledSize
    );
  }

  /**
   * Fill screen with gradient
   */
  drawGradient(
    x: number,
    y: number,
    width: number,
    height: number,
    colorTop: string,
    colorBottom: string
  ): void {
    const scaledX = x * this.scale;
    const scaledY = y * this.scale;
    const scaledWidth = width * this.scale;
    const scaledHeight = height * this.scale;

    const gradient = this.ctx.createLinearGradient(
      scaledX,
      scaledY,
      scaledX,
      scaledY + scaledHeight
    );
    gradient.addColorStop(0, colorTop);
    gradient.addColorStop(1, colorBottom);

    this.ctx.fillStyle = gradient;
    this.ctx.fillRect(scaledX, scaledY, scaledWidth, scaledHeight);
  }

  /**
   * Draw selection box (for menus)
   */
  drawSelectionBox(x: number, y: number, width: number, height: number): void {
    const scaledX = x * this.scale;
    const scaledY = y * this.scale;
    const scaledWidth = width * this.scale;
    const scaledHeight = height * this.scale;

    this.ctx.save();

    // Semi-transparent highlight
    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
    this.ctx.fillRect(scaledX, scaledY, scaledWidth, scaledHeight);

    // Border
    this.ctx.strokeStyle = '#ffffff';
    this.ctx.lineWidth = 2;
    this.ctx.strokeRect(scaledX, scaledY, scaledWidth, scaledHeight);

    this.ctx.restore();
  }

  /**
   * Draw modern styled card/window (matches sidebar theme)
   * Background: #0f3460 (dark blue)
   * Border: #533483 (purple)
   */
  drawCard(x: number, y: number, width: number, height: number, hasTitle: boolean = false): void {
    const scaledX = x * this.scale;
    const scaledY = y * this.scale;
    const scaledWidth = width * this.scale;
    const scaledHeight = height * this.scale;
    const borderWidth = 2 * this.scale;
    const borderRadius = 4 * this.scale;

    this.ctx.save();

    // Background - darker blue
    this.ctx.fillStyle = '#0f3460';
    this.roundRect(scaledX, scaledY, scaledWidth, scaledHeight, borderRadius);
    this.ctx.fill();

    // Border - purple
    this.ctx.strokeStyle = '#533483';
    this.ctx.lineWidth = borderWidth;
    this.roundRect(scaledX, scaledY, scaledWidth, scaledHeight, borderRadius);
    this.ctx.stroke();

    // Title bar line if has title
    if (hasTitle) {
      const titleHeight = 20 * this.scale;
      this.ctx.strokeStyle = '#533483';
      this.ctx.lineWidth = 1;
      this.ctx.beginPath();
      this.ctx.moveTo(scaledX, scaledY + titleHeight);
      this.ctx.lineTo(scaledX + scaledWidth, scaledY + titleHeight);
      this.ctx.stroke();
    }

    this.ctx.restore();
  }

  /**
   * Helper to draw rounded rectangle path
   */
  private roundRect(x: number, y: number, width: number, height: number, radius: number): void {
    this.ctx.beginPath();
    this.ctx.moveTo(x + radius, y);
    this.ctx.lineTo(x + width - radius, y);
    this.ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    this.ctx.lineTo(x + width, y + height - radius);
    this.ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    this.ctx.lineTo(x + radius, y + height);
    this.ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    this.ctx.lineTo(x, y + radius);
    this.ctx.quadraticCurveTo(x, y, x + radius, y);
    this.ctx.closePath();
  }
}
